<template>
  <d-container fluid class="main-content-container px-4 kpi-target-new">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('kpi.workersGoals') }}</span>
        <h3 class="page-title">{{ $t('kpi.addNewWorkerGoal') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/cele-pracownikow')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('kpi.addKpi') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer
              v-if="allDataLoaded"
              ref="kpi-target-new-form" tag="form">
              <d-row>
                <d-col col sm="6">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('users.worker') }}:
                    </label>

                    <select-user
                      ref="select-user"
                      :userID="user_id"
                      style="width: 100%;" />
                  </div>
                </d-col>

                <d-col col sm="6">
                  <div class="form-group">
                    <label for="email">
                      {{ $t('kpi.period') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="period"
                      v-slot="{ errors }">
                      <d-datepicker
                        v-model="period"
                        :monday-first="true"
                        format="MMMM yyyy"
                        :placeholder="$t('ui.chooseDate')"
                        minimum-view="month"
                        class="w-100"
                        :input-class="{ 'form-control': true, 'is-invalid': errors.length }"
                        :language="translationsForDatePicker" />
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="email">
                  {{ $t('kpi.contactsNumberRegardingNewOrders') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="new_orders_contacts_count"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    :state="errors.length ? 'invalid' : null"
                    v-model="new_orders_contacts_count" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('kpi.numberOfStartedOrders') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="started_orders_count"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    :state="errors.length ? 'invalid' : null"
                    v-model="started_orders_count" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('kpi.amountOfStartedOrders') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="started_orders_amount"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    :state="errors.length ? 'invalid' : null"
                    v-model="started_orders_amount" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('kpi.numberOfStartedOffers') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="started_offers_count"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    :state="errors.length ? 'invalid' : null"
                    v-model="started_offers_count" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('kpi.numberOfNewCustomers') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="new_customers_count"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    :state="errors.length ? 'invalid' : null"
                    v-model="new_customers_count" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('kpi.numberOfNewContacts') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="new_contacts_count"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    :state="errors.length ? 'invalid' : null"
                    v-model="new_contacts_count" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>
          </d-tab>
        </d-tabs>

        <div v-if="duplicateMode && !allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="duplicateMode && loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import SelectUser from './../common/SelectUser.vue';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'kpi-target-new',
  components: {
    'select-user': SelectUser
  },
  computed: {
    allDataLoaded () {
      if (this.duplicateMode) {
        return this.loadedData;
      }

      return true;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    duplicateMode () {
      return !!this.$route.params.duplicate_from_id;
    }
  },
  data () {
    return {
      user_id: null,
      period: null,
      new_orders_contacts_count: '',
      started_orders_count: '',
      started_orders_amount: '',
      started_offers_count: '',
      new_customers_count: '',
      new_contacts_count: '',
      loadedData: false,
      loadError: false
    };
  },
  mounted () {
    if (this.duplicateMode) {
      this.loadDuplicateSource();
    }
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['kpi-target-new-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        user_id: this.$refs['select-user'].user_id,
        year: this.period.getFullYear(),
        month: this.period.getMonth() + 1,
        new_orders_contacts_count: this.new_orders_contacts_count,
        started_orders_count: this.started_orders_count,
        started_orders_amount: this.started_orders_amount,
        started_offers_count: this.started_offers_count,
        new_customers_count: this.new_customers_count,
        new_contacts_count: this.new_contacts_count
      };

      if (!formDataToSend.user_id) {
        Vue.swal({
          title: this.$t('ui.saveErrorTitle'),
          html: this.$t('kpi.noUserSelectedKpiErrorText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/kpi/targets/store',
        formData: formDataToSend,
        successTitle: this.$t('kpi.addKpiSuccessTitle'),
        successText: this.$t('kpi.addKpiSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('kpi.addKpiErrorText'),
        successAction: () => {
          this.$router.push('/cele-pracownikow')
        }
      });
    },
    loadDuplicateSource () {
      FormUtils.loadItemData(this, {
        endpointBase: '/kpi/targets/item/',
        overrideID: parseInt(this.$route.params.duplicate_from_id, 10),
        instanceFields: ['user_id', 'new_orders_contacts_count', 'started_orders_count', 'started_orders_amount', 'new_customers_count', 'new_contacts_count'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        successAction: (response) => {
          this.period = new Date(response.year, response.month - 1);
        }
      });
    }
  }
};
</script>

<style lang="scss">
.kpi-target-new .vdp-datepicker .form-control[readonly] {
  background: #fff!important;
}
</style>
